/**
 * Domain models for the guided registration → KYC → agent-enrollment flow.
 * See the build spec (Onboarding & Registration Flow) for the screen map.
 */

import { IExtractedAddress, IExtractedShareCapital } from '../../core/models/IExtractedDocumentData.interface';

export type AccountType = 'individual' | 'business';

export type KycStatus = 'idle' | 'pending' | 'accepted' | 'declined';

/**
 * The screens of the sign-up wizard, in order. Azure B2C owns the first three (email, code,
 * password: sign-up step 1 "Account"), so the app starts at AccountType.
 *
 * The client sees 6 numbered steps (SIGNUP_STEPS); Kyc is the "Checks complete" screen between
 * steps 3 and 4 (businesses only) and Complete is "Your account is ready" after step 6.
 */
export enum OnboardingStep {
  Email = 0,
  EmailVerify = 1,
  Password = 2,
  AccountType = 3,
  Details = 4,
  Kyc = 5,
  Phone = 6,
  ProfileCompletion = 7,
  Review = 8,
  Complete = 9,
}

/** The 6 steps the client sees, numbered the same on every screen (and on the B2C pages). */
export interface SignupStep {
  label: string;
  /** Business accounts call step 3 "Company and you"; individuals "Your details". */
  individualLabel?: string;
  /** Screens that belong to this step. */
  screens: OnboardingStep[];
}

export const SIGNUP_STEPS: SignupStep[] = [
  { label: 'Account', screens: [OnboardingStep.Email, OnboardingStep.EmailVerify, OnboardingStep.Password] },
  { label: 'Account type', screens: [OnboardingStep.AccountType] },
  { label: 'Company and you', individualLabel: 'Your details', screens: [OnboardingStep.Details, OnboardingStep.Kyc] },
  { label: 'Mobile number', screens: [OnboardingStep.Phone] },
  { label: 'Address', screens: [OnboardingStep.ProfileCompletion] },
  { label: 'Review', screens: [OnboardingStep.Review] },
];

/** 1-based step number for a screen, for "Step 3 of 6". */
export function signupStepNumber(screen: OnboardingStep): number {
  return SIGNUP_STEPS.findIndex((s) => s.screens.includes(screen)) + 1;
}

/** A person's roles in the company they're signing up for (choose all that apply). */
export const SIGNUP_ROLES = [
  'Director',
  'Shareholder',
  'Ultimate beneficial owner',
  'Secretary',
  'Authorised representative',
] as const;

export interface OnboardingEntity {
  /** Present when an existing entity was selected from search; absent when creating new. */
  id?: string;
  name: string;
  registrationNumber?: string;
  jurisdiction?: string;
  entityType?: string;
  /**
   * Cyprus registry organisation type code (C company, P partnership, B business name, O overseas
   * company), when the entity came from the registry. Maps the type onto the form's Business Type
   * option even when the registry's text (e.g. "Εταιρεία") matches no option.
   */
  organisationTypeCode?: string;
  incorporationDate?: string; // yyyy-MM-dd
  /** The name in Greek, where registered or printed so; `name` is then its Latin form. */
  nameGreek?: string;
}

export interface OnboardingProfile {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  /**
   * The address as printed in Greek, where it was: the fields above are then its Greeklish. Saved
   * as streetGreek / apartmentGreek / cityGreek on the address answer.
   */
  addressLine1Greek?: string;
  addressLine2Greek?: string;
  cityGreek?: string;
  /**
   * Roles in the entity (entity accounts only), joined with ", ". Kept as one string for the
   * profile shape; the wizard edits OnboardingState.roles.
   * Saved through saveRegistration as rolesInCompany.
   */
  role?: string;
}

export interface AgentApplication {
  payoutMethod?: string; // 'bank' | 'crypto' | 'wallet'
  referralSource?: string;
  acceptedAgentTerms?: boolean;

  // Bank transfer details.
  accountHolder?: string;
  bankName?: string;
  iban?: string;
  swift?: string;

  // Crypto payout details.
  cryptoNetwork?: string; // e.g. Ethereum (ERC-20), Tron (TRC-20), Bitcoin
  cryptoAsset?: string; // e.g. USDT, USDC, BTC, ETH
  walletAddress?: string;
}

/**
 * Cumulative payload kept across screens so refresh / back-nav doesn't lose data.
 * NOTE: the password is intentionally NOT part of this (never persisted to
 * sessionStorage); it's held in a transient signal in OnboardingStateService.
 */
export interface OnboardingState {
  step: OnboardingStep;
  email: string;
  emailVerified: boolean;
  phone: string;
  phoneVerified: boolean;
  firstName: string;
  lastName: string;
  /** The name in Greek, where typed or read so; firstName/lastName are then its Greeklish. */
  firstNameGreek?: string;
  lastNameGreek?: string;
  /**
   * Individuals: date of birth (yyyy-MM-dd) and an ID or passport number. With the name they're
   * how an existing client is recognised (a name alone never matches), so they're required.
   */
  dateOfBirth: string;
  /** Which document idNumber is from. */
  idType: IdDocumentType;
  idNumber: string;
  /**
   * Individuals: read off the document uploaded on "Your details" (not asked in the wizard), so the
   * application's Personal details start with them filled. As the document states them.
   */
  gender?: string;
  nationality?: string;
  /** Individuals: the expiry date (yyyy-MM-dd) and issuing country of the document read. */
  idExpiry?: string;
  idIssuingCountry?: string;
  accountType: AccountType;
  /** Business accounts: the signer's roles in the company, any combination of SIGNUP_ROLES. */
  roles: string[];
  /** Business accounts: registry/company details. */
  entity?: OnboardingEntity;
  /** Business accounts: the people the company's papers name, added as officers once it exists. */
  companyPeople?: SignupPerson[];
  companyFacts?: SignupCompanyFacts;
  /** Directory object id returned by create-account (B2C local account). */
  azureObjectId?: string;
  profile?: OnboardingProfile;
  onboardingId?: string;
  /**
   * Whether the sign-up screening ran ('pending' once it has: the outcome is never sent to the
   * browser, see AmlScreeningReceipt) and its reference for support. The result itself is stored by
   * the Identity API and attached to the client form server-side for compliance.
   */
  kycStatus: KycStatus;
  kycReference?: string;
  termsAccepted: boolean;
  becomeAgent?: boolean;
  agentApplication?: AgentApplication;
}

/**
 * A document uploaded on "Your details", kept until the application exists and then filed in the
 * client's DMS. `kind` is one of DOCUMENT_KINDS (PASSPORT for a passport or ID card).
 */
export interface SignupDocument {
  file: File;
  kind: string;
  /** What it is in words, filed as its description ("Certificate of incorporation"). */
  title?: string;
}

/**
 * Business sign-up: a person the company's papers name, and what the signer said about them. Added
 * as the business's officer when the application is created (OnboardingSubmitService).
 */
export interface SignupPerson {
  fullName: string;
  firstName: string;
  lastName: string;
  /** The name as printed in Greek, where it was; the names above are then its Greeklish. */
  firstNameGreek?: string;
  lastNameGreek?: string;
  /** Director, Secretary, Shareholder, Ultimate beneficial owner. */
  roles: string[];
  /** A company holding shares or acting as secretary: not matched or added here. */
  isOrganisation: boolean;
  sharesHeld?: string;
  shareClass?: string;
  /** Shareholders and UBOs: the percentage the papers give; else worked out from the shares. */
  sharePercentage?: string;
  /** A nominee: who they hold their shares for. */
  nomineeFor?: string;
  /** yyyy-MM-dd */
  appointedOn?: string;
  dateOfBirth?: string;
  placeOfBirth?: string;
  nationality?: string;
  gender?: string;
  idNumber?: string;
  idExpiry?: string;
  passportNumber?: string;
  passportExpiry?: string;
  occupation?: string;
  email?: string;
  phone?: string;
  isPep?: string;
  taxIdNumber?: string;
  socialInsuranceNumber?: string;
  idIssuingCountry?: string;
  passportIssuingCountry?: string;
  /** Where they live, as the papers print it (Greek kept as printed). */
  address?: IExtractedAddress;
  /** Clients on file with this name: id and name only. */
  matches: { individualId: number; fullName: string }[];
  /**
   * The answer to "Is this them?": the individual it is, null for "No, someone new", undefined while
   * not answered. Not asked when there are no matches.
   */
  linkTo?: number | null;
}

/** Business sign-up: what the papers say that adding the people needs. */
export interface SignupCompanyFacts {
  /** yyyy-MM-dd, the fallback appointment date. */
  incorporationDate?: string;
  /** All issued shares, to turn a shareholder's shares into a percentage. */
  totalIssuedShares?: number;
  /** The share capital the client confirmed, saved as the company's setup when it has none yet. */
  shareCapital?: IExtractedShareCapital;
}

/** Which upload panel on "Your details" a document came from. */
export type SignupDocumentSource = 'identity' | 'company';

/** The identity document an individual gives the number of at sign-up. */
export type IdDocumentType = 'idCard' | 'passport';

export const ID_DOCUMENT_LABELS: Record<IdDocumentType, string> = {
  idCard: 'ID card',
  passport: 'Passport',
};

export const initialOnboardingState: OnboardingState = {
  // Account creation (email + code + password) is owned by the Azure B2C sign-up
  // flow, so the in-app wizard starts at Account Type.
  step: OnboardingStep.AccountType,
  email: '',
  emailVerified: false,
  phone: '',
  phoneVerified: false,
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  idType: 'idCard',
  idNumber: '',
  accountType: 'business',
  roles: [],
  kycStatus: 'idle',
  termsAccepted: false,
};

// ── API contracts ───────────────────────────────────────────────────────────

export interface EntitySearchResult {
  id: string;
  name: string;
  registrationNumber?: string;
  jurisdiction?: string;
  /** Raw organisation type from the Cyprus registry (e.g. for auto-filling entity type). */
  organisationType?: string;
  /** Registry organisation type code: C company, P partnership, B business name, O overseas company. */
  organisationTypeCode?: string;
  /** Registration / incorporation date from the registry (raw string). */
  registrationDate?: string;
  /** The registry's Greek name for the organisation, when it has one. */
  nameGreek?: string;
}

export interface CreateAccountPayload {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}

export interface CreateAccountResponse {
  azureObjectId: string;
}

/**
 * Mirrors BC_Identity `BusinessAmlScreeningRequest`.
 * POST {IdentityAPI}/ShuftiProAml/screen
 */
export interface AmlScreeningRequest {
  businessName: string;
  businessIncorporationDate?: string; // yyyy-MM-dd
  uniqueId?: string;
  countries?: string[]; // ISO 3166-1 alpha-2
  filters?: string[];
  matchScore?: number;
  aliasSearch?: boolean;
  rcaSearch?: boolean;
  ongoing?: boolean;
  ongoingAdverseMedia?: boolean;
  context?: string;
}

/**
 * Mirrors BC_Identity `AmlScreeningReceipt`: the screening reference and nothing about the outcome.
 * The Identity API stores ShuftiPro's result itself (keyed by the signed-in customer) and the
 * Corporate API attaches it to the client form for compliance, so the browser never holds it
 * (tipping-off rule) and cannot alter it.
 */
export interface AmlScreeningReceipt {
  reference?: string | null;
}

/**
 * Mirrors BC_Corporate `CustomerOnboardingRecordVm` (customer/forms/{id}/onboarding): what the
 * client did, as they may see it. Never carries the screening.
 */
export interface CustomerOnboardingRecord {
  termsAcceptedAt?: string | null;
  submittedAt?: string | null;
  declarationAcceptedAt?: string | null;
}
